import { useCallback, useRef, useEffect } from 'react'
import { DebouncedFunc, debounce as lodashDebounce } from 'lodash'

/**
 * A custom React hook that provides debouncing functionality to delay the execution of a callback function.
 * This is useful for scenarios like search input fields, API calls, or any operation that should be
 * delayed until the user stops performing an action for a specified period.
 *
 * @template T - The type of the value being debounced
 * @param {number} delay - The delay in milliseconds before the callback is executed. Defaults to 500ms.
 * @returns {(value: T, callback: (debouncedValue: T) => void) => void} A debounced function that accepts a value and callback
 *
 * @example
 * ```typescript
 * const debounce = useDebounce<string>(300);
 *
 * const handleSearch = (searchTerm: string) => {
 *   debounce(searchTerm, (debouncedTerm) => {
 *     // This will only execute 300ms after the last call
 *     performSearch(debouncedTerm);
 *   });
 * };
 * ```
 *
 * @example
 * ```typescript
 * // Usage in a search input component
 * const SearchInput = () => {
 *   const [searchTerm, setSearchTerm] = useState('');
 *   const debounce = useDebounce<string>(500);
 *
 *   const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
 *     const value = event.target.value;
 *     setSearchTerm(value);
 *
 *     debounce(value, (debouncedValue) => {
 *       // API call will only happen 500ms after user stops typing
 *       searchAPI(debouncedValue);
 *     });
 *   };
 *
 *   return <input onChange={handleInputChange} value={searchTerm} />;
 * };
 * ```
 */
function useDebounce<T>(delay = 500) {
	const callbackRef = useRef<((debouncedValue: T) => void) | null>(
		null
	)
	const valueRef = useRef<T>()

	const debouncedFunction = useRef<DebouncedFunc<() => void> | null>(
		null
	)

	const debounce = useCallback(
		(value: T, callback: (debouncedValue: T) => void) => {
			valueRef.current = value
			callbackRef.current = callback

			debouncedFunction.current ??= lodashDebounce(
				() => {
					if (callbackRef.current && valueRef.current !== undefined) {
						callbackRef.current(valueRef.current)
					}
				},
				delay,
				{
					leading: false,
					trailing: true,
				}
			)

			debouncedFunction.current()
		},
		[delay]
	)

	useEffect(() => {
		if (debouncedFunction.current) {
			debouncedFunction.current.cancel()
			debouncedFunction.current = null
		}
		return () => {
			if (debouncedFunction.current) {
				debouncedFunction.current.cancel()
			}
		}
	}, [delay])

	return debounce
}

export default useDebounce
